import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Upload, CheckCircle, FileText, Loader2, AlertCircle, Download,
  Plus, X, Eye, Pencil, FileDown, DollarSign, Trash2, ExternalLink, Link2, UserPlus, Move
} from 'lucide-react';
import axios from 'axios';
import JumboOwnerOverrideDialog from '../applications/JumboOwnerOverrideDialog';
import MoveFileDialog from '../applications/MoveFileDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_FILE_SIZE = 25 * 1024 * 1024;

/**
 * Reusable document upload component with multi-file, debt schedule, and PFS support.
 *
 * Props:
 * - applicationId: string
 * - token: string (for public uploads)
 * - requiredDocuments: array of RequiredDocument objects
 * - onUploadComplete: callback after successful upload
 * - authToken: string (JWT for authenticated requests)
 * - debtSchedule: array of debt schedule items (for the debt schedule panel)
 * - debtSchedulePdfUrl: string (URL to download debt schedule PDF)
 * - onDebtScheduleSave: callback(items) when debt schedule is saved
 * - pfsData: object (personal financial statement data)
 * - pfsPdfUrl: string (URL to download PFS PDF)
 * - onPfsSave: callback() when PFS is saved
 * - canPushPfs: boolean (whether the user can push PFS to broker/borrower)
 */
const DocumentUploader = ({
  applicationId, token, requiredDocuments = [], onUploadComplete,
  authToken, debtSchedule = [], debtSchedulePdfUrl, onDebtScheduleSave,
  documentLink, pfsData = {}, pfsPdfUrl, onPfsSave, canPushPfs = false,
  additionalDocuments = [], form8821s = [], isUnderwriter = false,
  canDeleteFiles = true,
  productType = '', merchantPreAppData = null, additionalMerchantPreApps = [], onMerchantPreAppSave
}) => {
  const [uploading, setUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [errors, setErrors] = useState({});
  const [markingInLink, setMarkingInLink] = useState({});
  const [ownerOverrideOpen, setOwnerOverrideOpen] = useState(false);
  const [movingFile, setMovingFile] = useState(null);   // { file, fromBucket, fromSlot }
  const fileInputRefs = useRef({});

  const handleUpload = async (docName, file) => {
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      setErrors(prev => ({ ...prev, [docName]: 'File exceeds the 25 MB limit' }));
      return;
    }
    setErrors(prev => ({ ...prev, [docName]: null }));
    setUploading(prev => ({ ...prev, [docName]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      let url;
      const headers = {};
      if (token) {
        url = `${BACKEND_URL}/api/public/upload/${token}?document_type=${encodeURIComponent(docName)}`;
      } else {
        url = `${BACKEND_URL}/api/upload/${applicationId}?document_type=${encodeURIComponent(docName)}`;
        if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      }
      await axios.post(url, formData, { headers });
      if (onUploadComplete) onUploadComplete(docName);
    } catch (err) {
      console.error('Upload error:', err.response?.status, err.response?.data, err.message);
      let msg;
      if (err.response?.status === 413) {
        msg = 'File exceeds the 25 MB limit';
      } else if (err.response?.status === 422) {
        msg = 'Upload rejected by server. Please try a different file.';
      } else if (!err.response) {
        msg = 'Network error. Check your connection and try again.';
      } else {
        msg = err.response?.data?.detail || `Upload failed (${err.response?.status}). Please try again.`;
      }
      setErrors(prev => ({ ...prev, [docName]: msg }));
    } finally {
      setUploading(prev => ({ ...prev, [docName]: false }));
      setUploadProgress(prev => ({ ...prev, [docName]: null }));
    }
  };

  const handleMultiUpload = async (docName, files) => {
    if (!files || files.length === 0) return;
    const fileList = Array.from(files);
    // Validate all files first
    const oversized = fileList.filter(f => f.size > MAX_FILE_SIZE);
    if (oversized.length > 0) {
      setErrors(prev => ({ ...prev, [docName]: `${oversized.length} file(s) exceed the 25 MB limit` }));
      return;
    }
    setErrors(prev => ({ ...prev, [docName]: null }));
    setUploading(prev => ({ ...prev, [docName]: true }));
    let failed = 0;
    for (let i = 0; i < fileList.length; i++) {
      setUploadProgress(prev => ({ ...prev, [docName]: `${i + 1} of ${fileList.length}` }));
      try {
        const formData = new FormData();
        formData.append('file', fileList[i]);
        let url;
        const headers = {};
        if (token) {
          url = `${BACKEND_URL}/api/public/upload/${token}?document_type=${encodeURIComponent(docName)}`;
        } else {
          url = `${BACKEND_URL}/api/upload/${applicationId}?document_type=${encodeURIComponent(docName)}`;
          if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
        }
        await axios.post(url, formData, { headers });
      } catch (err) {
        console.error(`Upload error for ${fileList[i].name}:`, err);
        failed++;
      }
    }
    if (failed > 0) {
      setErrors(prev => ({ ...prev, [docName]: `${failed} of ${fileList.length} file(s) failed to upload` }));
    }
    if (onUploadComplete) onUploadComplete(docName);
    setUploading(prev => ({ ...prev, [docName]: false }));
    setUploadProgress(prev => ({ ...prev, [docName]: null }));
  };

  const handleMarkInUploadLink = async (docName) => {
    setMarkingInLink(prev => ({ ...prev, [docName]: true }));
    try {
      const headers = {};
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      await axios.put(
        `${BACKEND_URL}/api/applications/${applicationId}/mark-in-upload-link`,
        { document_name: docName, in_upload_link: true },
        { headers }
      );
      if (onUploadComplete) onUploadComplete(docName);
    } catch (err) {
      setErrors(prev => ({ ...prev, [docName]: err.response?.data?.detail || 'Failed to mark document' }));
    } finally {
      setMarkingInLink(prev => ({ ...prev, [docName]: false }));
    }
  };

  const [deleting, setDeleting] = useState({});

  const handleDeleteFile = async (docName, storedFilename) => {
    if (!window.confirm('Delete this file?')) return;
    const key = `${docName}_${storedFilename}`;
    setDeleting(prev => ({ ...prev, [key]: true }));
    try {
      const headers = {};
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      await axios.delete(
        `${BACKEND_URL}/api/applications/${applicationId}/documents/${encodeURIComponent(docName)}/files/${encodeURIComponent(storedFilename)}`,
        { headers }
      );
      if (onUploadComplete) onUploadComplete(docName);
    } catch (err) {
      setErrors(prev => ({ ...prev, [docName]: err.response?.data?.detail || 'Failed to delete file' }));
    } finally {
      setDeleting(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleDeleteAllFiles = async (docName) => {
    if (!window.confirm(`Remove all files for "${docName}"?`)) return;
    setDeleting(prev => ({ ...prev, [docName]: true }));
    try {
      const headers = {};
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      await axios.delete(
        `${BACKEND_URL}/api/applications/${applicationId}/documents/${encodeURIComponent(docName)}`,
        { headers }
      );
      if (onUploadComplete) onUploadComplete(docName);
    } catch (err) {
      setErrors(prev => ({ ...prev, [docName]: err.response?.data?.detail || 'Failed to delete document' }));
    } finally {
      setDeleting(prev => ({ ...prev, [docName]: false }));
    }
  };

  const handleFileSelect = (docName) => {
    const input = fileInputRefs.current[docName];
    if (input) { input.value = ''; input.click(); }
  };

  const getDownloadUrl = (storedFilename) => {
    if (!storedFilename) return null;
    return `/api/files/${applicationId}/${storedFilename}`;
  };

  if (!requiredDocuments || requiredDocuments.length === 0) return null;

  // Split into categories. Match on the flag OR the document name so existing
  // records saved before `is_debt_schedule` was persisted still surface here
  // (mirrors the PFS fallback pattern below).
  const isDebtScheduleDoc = (d) =>
    d.is_debt_schedule || (d.name && d.name.toLowerCase().includes('debt schedule'));
  const debtDocs = requiredDocuments.filter(isDebtScheduleDoc);
  const pfsDocs = requiredDocuments.filter(d => d.is_pfs || (d.name && d.name.toLowerCase().includes('personal financial')));
  const mpPreAppDocs = requiredDocuments.filter(d => d.is_mp_pre_app || (d.name && d.name.toLowerCase().includes('pre merchant processing')));
  const uploadDocs = requiredDocuments.filter(d =>
    !isDebtScheduleDoc(d) && !d.is_pfs && !d.is_mp_pre_app &&
    !(d.name && (d.name.toLowerCase().includes('personal financial') || d.name.toLowerCase().includes('pre merchant processing')))
  );
  const pendingDocs = uploadDocs.filter(d => !d.uploaded && !(d.files || []).length);
  const uploadedDocs = uploadDocs.filter(d => d.uploaded || (d.files || []).length > 0);

  return (
    <div className="space-y-4" data-testid="document-uploader">
      {/* Jumbo Term Loan — Ad-hoc "For Another Owner" download modal */}
      {productType === 'jumbo_term_loan' && authToken && !token && (
        <JumboOwnerOverrideDialog
          open={ownerOverrideOpen}
          onOpenChange={setOwnerOverrideOpen}
          applicationId={applicationId}
          authToken={authToken}
        />
      )}
      {/* Merchant Pre-App Section (merchant_processing only) */}
      {productType === 'merchant_processing' && mpPreAppDocs.map(doc => (
        <MerchantPreAppList
          key={doc.name}
          doc={doc}
          applicationId={applicationId}
          authToken={authToken}
          primaryData={merchantPreAppData || {}}
          additionalEntries={additionalMerchantPreApps || []}
          onSave={onMerchantPreAppSave || onUploadComplete}
        />
      ))}
      {/* Debt Schedule Section */}
      {debtDocs.map(doc => (
        <DebtScheduleCard
          key={doc.name}
          doc={doc}
          applicationId={applicationId}
          authToken={authToken}
          debtSchedule={debtSchedule}
          debtSchedulePdfUrl={debtSchedulePdfUrl}
          onSave={onDebtScheduleSave}
          productType={productType}
        />
      ))}

      {/* Personal Financial Statement Section */}
      {pfsDocs.map(doc => (
        <PFSCard
          key={doc.name}
          doc={doc}
          applicationId={applicationId}
          authToken={authToken}
          pfsData={pfsData}
          pfsPdfUrl={pfsPdfUrl}
          onSave={onPfsSave}
          canPush={canPushPfs}
        />
      ))}

      {/* 8821 Tax Authorization Forms Section — not applicable to Merchant Processing */}
      {!token && applicationId && productType !== 'merchant_processing' && (
        <Form8821Card
          applicationId={applicationId}
          authToken={authToken}
          form8821s={form8821s}
          onSave={onPfsSave || onUploadComplete}
          isUnderwriter={isUnderwriter}
        />
      )}

      {/* Pending Documents (multi-file) */}
      {pendingDocs.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-5" data-testid="pending-documents-section">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="h-4 w-4 text-orange-600" />
            <h4 className="text-sm font-bold text-orange-800">Documents Needed</h4>
            <span className="bg-orange-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
              {pendingDocs.length}
            </span>
          </div>
          <p className="text-xs text-slate-600 mb-3 ml-6">
            Upload the following documents to continue processing your application. Max 25 MB per file.
          </p>
          <div className="space-y-2 ml-6">
            {pendingDocs.map((doc, i) => (
              <div key={doc.name} className="flex items-center gap-3 px-3 py-3 bg-white border border-orange-200 rounded-lg" data-testid={`pending-doc-${i}`}>
                <FileText className="h-4 w-4 text-orange-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-slate-700">{doc.name}</span>
                    {doc.ad_hoc && (
                      <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 border border-orange-200" data-testid={`ad-hoc-badge-${i}`}>
                        Requested by underwriter
                      </span>
                    )}
                  </div>
                  {doc.ad_hoc && (doc.requested_by_name || doc.requested_at) && (
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {doc.requested_by_name && <span>Requested by {doc.requested_by_name}</span>}
                      {doc.requested_by_name && doc.requested_at && <span> · </span>}
                      {doc.requested_at && <span>{new Date(doc.requested_at).toLocaleDateString()}</span>}
                    </div>
                  )}
                </div>
                <input
                  ref={el => { fileInputRefs.current[doc.name] = el; }}
                  type="file"
                  className="hidden"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  onChange={e => handleMultiUpload(doc.name, e.target.files)}
                  data-testid={`file-input-${i}`}
                />
                {documentLink && !token && (
                  <Button size="sm" variant="outline" className="text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                    onClick={() => handleMarkInUploadLink(doc.name)} disabled={markingInLink[doc.name]} data-testid={`in-upload-link-btn-${i}`}>
                    {markingInLink[doc.name] ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Marking...</> : <><ExternalLink className="h-3 w-3 mr-1" /> In Upload Link</>}
                  </Button>
                )}
                {(() => {
                  const PDF_ROUTE_BY_TYPE = {
                    jumbo_term_loan: 'jumbo-term-loan',
                    term_loc_hybrid: 'term-loc-hybrid',
                    term_loan_3_5yr: 'term-loan-3-5yr',
                    sba_loan: 'sba-loan',
                  };
                  const pdfRoute = PDF_ROUTE_BY_TYPE[productType];
                  if (!(doc.name === 'Signed Application' && pdfRoute)) return null;
                  return (
                    <>
                      <Button size="sm" variant="outline" className="text-xs border-teal-400 text-teal-700 hover:bg-teal-50 font-medium"
                        onClick={() => {
                          const url = `${BACKEND_URL}/api/applications/${applicationId}/pdf/${pdfRoute}`;
                          const a = document.createElement('a');
                          a.href = url;
                          a.target = '_blank';
                          if (authToken) a.href += `?token=${authToken}`;
                          a.click();
                        }}
                        data-testid={`download-${pdfRoute}-application-btn`}
                      >
                        <FileDown className="h-3 w-3 mr-1" /> Download Application
                      </Button>
                      {productType === 'jumbo_term_loan' && authToken && !token && (
                        <Button size="sm" variant="outline" className="text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50 font-medium"
                          onClick={() => setOwnerOverrideOpen(true)}
                          data-testid={`download-jumbo-owner-override-btn-pending-${i}`}
                          title="Type a different owner's info and download a fresh application PDF just for them"
                        >
                          <UserPlus className="h-3 w-3 mr-1" /> For Another Owner
                        </Button>
                      )}
                    </>
                  );
                })()}
                <Button size="sm" variant="outline" className="text-xs border-orange-300 text-orange-700 hover:bg-orange-100"
                  onClick={() => handleFileSelect(doc.name)} disabled={uploading[doc.name]} data-testid={`upload-btn-${i}`}>
                  {uploading[doc.name]
                    ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Uploading...</>
                    : <><Upload className="h-3 w-3 mr-1" /> Upload</>}
                </Button>
                {errors[doc.name] && <span className="text-xs text-red-600">{errors[doc.name]}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Uploaded Documents (multi-file support) */}
      {uploadedDocs.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="uploaded-documents-section">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle className="h-4 w-4 text-emerald-500" />
            <h4 className="text-sm font-bold text-slate-700">Documents Uploaded</h4>
            <span className="bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
              {uploadedDocs.length}
            </span>
          </div>
          <div className="space-y-3 ml-6">
            {uploadedDocs.map((doc, i) => {
              const files = doc.files || [];
              const hasMultipleFiles = files.length > 1;
              const isInLink = !!doc.in_upload_link;
              const hasFiles = files.length > 0 || !!doc.filename;
              // Show "In Upload Link" badge if explicitly marked OR if the app has a document link set
              const showLinkBadge = isInLink || (!!documentLink && !hasFiles);
              const bgClass = isInLink ? 'bg-blue-50 border-blue-100' : (documentLink && hasFiles) ? 'bg-emerald-50 border-emerald-100' : 'bg-emerald-50 border-emerald-100';
              return (
                <div key={doc.name} className={`${bgClass} border rounded-lg p-3`} data-testid={`uploaded-doc-${i}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {isInLink
                      ? <ExternalLink className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
                      : <CheckCircle className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                    }
                    <span className="text-sm font-medium text-slate-700 flex-1">{doc.name}</span>
                    {doc.ad_hoc && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 border border-orange-200" title={doc.requested_by_name ? `Requested by ${doc.requested_by_name}${doc.requested_at ? ' on ' + new Date(doc.requested_at).toLocaleDateString() : ''}` : 'Requested by underwriter'} data-testid={`ad-hoc-uploaded-badge-${i}`}>
                        Ad-hoc request
                      </span>
                    )}
                    {isInLink && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium" data-testid={`in-link-badge-${i}`}>Found in Document Upload Link</span>
                    )}
                    {!isInLink && !!documentLink && hasFiles && (
                      <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-medium border border-blue-200" data-testid={`also-in-link-badge-${i}`}>Found in Document Upload Link</span>
                    )}
                    {hasMultipleFiles && (
                      <span className="text-xs text-emerald-600 font-medium">{files.length} files</span>
                    )}
                    {/* Add another file button */}
                    <input
                      ref={el => { fileInputRefs.current[`add_${doc.name}`] = el; }}
                      type="file"
                      className="hidden"
                      multiple
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                      onChange={e => handleMultiUpload(doc.name, e.target.files)}
                    />
                    <Button
                      size="sm" variant="ghost"
                      className={`h-6 px-2 text-xs ${isInLink ? 'text-blue-600 hover:text-blue-800 hover:bg-blue-100' : 'text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100'}`}
                      onClick={() => { const inp = fileInputRefs.current[`add_${doc.name}`]; if (inp) { inp.value = ''; inp.click(); } }}
                      disabled={uploading[doc.name]}
                      data-testid={`add-file-btn-${i}`}
                    >
                      {uploading[doc.name]
                        ? <><Loader2 className="h-3 w-3 animate-spin" /></>
                        : <><Plus className="h-3 w-3 mr-0.5" /> Add Files</>}
                    </Button>
                    {(() => {
                      const PDF_ROUTE_BY_TYPE = {
                        jumbo_term_loan: 'jumbo-term-loan',
                        term_loc_hybrid: 'term-loc-hybrid',
                        term_loan_3_5yr: 'term-loan-3-5yr',
                        sba_loan: 'sba-loan',
                      };
                      const pdfRoute = PDF_ROUTE_BY_TYPE[productType];
                      if (!(doc.name === 'Signed Application' && pdfRoute)) return null;
                      return (
                        <>
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-teal-600 hover:text-teal-800 hover:bg-teal-50"
                            onClick={() => {
                              const url = `${BACKEND_URL}/api/applications/${applicationId}/pdf/${pdfRoute}`;
                              const a = document.createElement('a');
                              a.href = url;
                              a.target = '_blank';
                              if (authToken) a.href += `?token=${authToken}`;
                              a.click();
                            }}
                            data-testid={`download-${pdfRoute}-uploaded-btn`}
                          >
                            <FileDown className="h-3 w-3 mr-0.5" /> Download Application
                          </Button>
                          {productType === 'jumbo_term_loan' && authToken && !token && (
                            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50"
                              onClick={() => setOwnerOverrideOpen(true)}
                              data-testid={`download-jumbo-owner-override-btn-uploaded-${i}`}
                              title="Type a different owner's info and download a fresh application PDF just for them"
                            >
                              <UserPlus className="h-3 w-3 mr-0.5" /> For Another Owner
                            </Button>
                          )}
                        </>
                      );
                    })()}
                    {/* Delete all files for this document */}
                    {authToken && applicationId && !token && canDeleteFiles && (
                      <Button
                        size="sm" variant="ghost"
                        className="h-6 px-2 text-xs text-red-400 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteAllFiles(doc.name)}
                        disabled={deleting[doc.name]}
                        data-testid={`delete-doc-btn-${i}`}
                      >
                        {deleting[doc.name] ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                      </Button>
                    )}
                  </div>
                  {/* In Upload Link message */}
                  {isInLink && (
                    <p className="text-xs text-blue-600 ml-5">Available in the document upload link</p>
                  )}
                  {/* List individual files */}
                  {files.length > 0 ? (
                    <div className="space-y-1 ml-5">
                      {files.map((f, fi) => (
                        <div key={fi} className="flex items-center gap-2 text-xs text-slate-600 py-1">
                          <FileText className="h-3 w-3 text-slate-400 flex-shrink-0" />
                          <span className="truncate flex-1">{f.filename}</span>
                          {f.owner_name && (
                            <span className="text-[10px] bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0" data-testid={`file-owner-${i}-${fi}`}>
                              Signed by {f.owner_name}
                            </span>
                          )}
                          {applicationId && f.stored_filename && (
                            <a
                              href={getDownloadUrl(f.stored_filename)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 transition-colors flex-shrink-0"
                              data-testid={`download-file-${i}-${fi}`}
                              title={`Download ${f.filename}`}
                            >
                              <Download className="h-3.5 w-3.5" />
                              Download
                            </a>
                          )}
                          {authToken && applicationId && !token && f.stored_filename && canDeleteFiles && (
                            <button
                              onClick={() => setMovingFile({ file: f, fromBucket: 'required', fromSlot: doc.name })}
                              className="inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-400 hover:text-slate-800 hover:bg-slate-100 transition-colors flex-shrink-0"
                              data-testid={`move-file-${i}-${fi}`}
                              title="Move to a different slot"
                              aria-label="Move file to a different slot"
                            >
                              <Move className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {authToken && applicationId && !token && f.stored_filename && canDeleteFiles && (
                            <button
                              onClick={() => handleDeleteFile(doc.name, f.stored_filename)}
                              disabled={deleting[`${doc.name}_${f.stored_filename}`]}
                              className="ml-1 inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0 disabled:opacity-50"
                              data-testid={`delete-file-${i}-${fi}`}
                              title="Delete file"
                              aria-label="Delete file"
                            >
                              {deleting[`${doc.name}_${f.stored_filename}`]
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <Trash2 className="h-3.5 w-3.5" />}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : doc.filename ? (
                    <div className="ml-5 flex items-center gap-2 text-xs text-slate-600 py-1">
                      <FileText className="h-3 w-3 text-slate-400" />
                      <span className="truncate flex-1">{doc.filename}</span>
                      {doc.stored_filename && applicationId && (
                        <a
                          href={getDownloadUrl(doc.stored_filename)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 transition-colors flex-shrink-0"
                          title={`Download ${doc.filename}`}
                        >
                          <Download className="h-3.5 w-3.5" />
                          Download
                        </a>
                      )}
                      {authToken && applicationId && !token && doc.stored_filename && canDeleteFiles && (
                        <button
                          onClick={() => handleDeleteFile(doc.name, doc.stored_filename)}
                          disabled={deleting[`${doc.name}_${doc.stored_filename}`]}
                          className="ml-1 inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0 disabled:opacity-50"
                          data-testid={`delete-legacy-file-${i}`}
                          title="Delete file"
                          aria-label="Delete file"
                        >
                          {deleting[`${doc.name}_${doc.stored_filename}`]
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <Trash2 className="h-3.5 w-3.5" />}
                        </button>
                      )}
                    </div>
                  ) : null}
                  {errors[doc.name] && <p className="text-xs text-red-600 ml-5 mt-1">{errors[doc.name]}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* All complete banner */}
      {pendingDocs.length === 0 && debtDocs.every(d => d.uploaded) && uploadedDocs.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3" data-testid="all-docs-complete">
          <CheckCircle className="h-5 w-5 text-emerald-600" />
          <p className="text-sm font-medium text-emerald-800">All required documents have been uploaded!</p>
        </div>
      )}

      {/* Additional Documents Section */}
      {!token && applicationId && (
        <AdditionalDocumentsSection
          applicationId={applicationId}
          authToken={authToken}
          additionalDocuments={additionalDocuments}
          onUploadComplete={onUploadComplete}
          onRequestMove={(f, slotName) => setMovingFile({ file: f, fromBucket: 'additional', fromSlot: slotName })}
          requiredDocuments={requiredDocuments || []}
        />
      )}

      {/* Move-file modal */}
      {movingFile && authToken && !token && (
        <MoveFileDialog
          open={!!movingFile}
          onOpenChange={(v) => { if (!v) setMovingFile(null); }}
          applicationId={applicationId}
          authToken={authToken}
          file={movingFile.file}
          fromBucket={movingFile.fromBucket}
          fromSlot={movingFile.fromSlot}
          requiredDocs={requiredDocuments || []}
          additionalDocs={additionalDocuments || []}
          onMoved={() => { onUploadComplete && onUploadComplete(movingFile.fromSlot); setMovingFile(null); }}
        />
      )}
    </div>
  );
};


/** 8821 Tax Authorization Forms Section */
const Form8821Card = ({ applicationId, authToken, form8821s = [], onSave, isUnderwriter = false }) => {
  const [newLabel, setNewLabel] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadingSigned, setUploadingSigned] = useState({});
  const [deleting, setDeleting] = useState({});
  const unsignedFileRef = useRef(null);
  const signedFileRefs = useRef({});
  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

  const handleUploadUnsigned = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const label = newLabel.trim() || 'Business';
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(
        `${BACKEND_URL}/api/applications/${applicationId}/8821?label=${encodeURIComponent(label)}`,
        formData, { headers }
      );
      setNewLabel('');
      if (onSave) onSave();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (unsignedFileRef.current) unsignedFileRef.current.value = '';
    }
  };

  const handleUploadSigned = async (formId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingSigned(prev => ({ ...prev, [formId]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(
        `${BACKEND_URL}/api/applications/${applicationId}/8821/${formId}/signed`,
        formData, { headers }
      );
      if (onSave) onSave();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingSigned(prev => ({ ...prev, [formId]: false }));
      const ref = signedFileRefs.current[formId];
      if (ref) ref.value = '';
    }
  };

  const handleDelete = async (formId) => {
    if (!window.confirm('Delete this 8821 form entry?')) return;
    setDeleting(prev => ({ ...prev, [formId]: true }));
    try {
      await axios.delete(
        `${BACKEND_URL}/api/applications/${applicationId}/8821/${formId}`,
        { headers }
      );
      if (onSave) onSave();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(prev => ({ ...prev, [formId]: false }));
    }
  };

  const handleDownload = async (storedFilename, originalFilename) => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/files/${applicationId}/${storedFilename}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (!resp.ok) throw new Error('Download failed');
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = originalFilename || storedFilename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download file');
    }
  };

  const pendingCount = form8821s.filter(f => f.status === 'pending_signature').length;
  const signedCount = form8821s.filter(f => f.status === 'signed').length;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-5" data-testid="form-8821-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-amber-700" />
          <h4 className="text-sm font-bold text-amber-900">IRS Form 8821 — Tax Authorization</h4>
          {form8821s.length > 0 && (
            <span className="text-xs text-amber-600 ml-1">
              {signedCount}/{form8821s.length} signed
            </span>
          )}
        </div>
      </div>
      <p className="text-xs text-amber-700 mb-3 ml-6">
        {isUnderwriter
          ? 'Upload unsigned 8821 forms for each entity/owner. Brokers and borrowers will download, get them wet-signed, and upload back.'
          : 'Download each 8821 form, get it wet-signed, then upload the signed version back.'}
      </p>

      {/* Underwriter: Add new form */}
      {isUnderwriter && (
        <div className="flex items-center gap-2 ml-6 mb-3">
          <Input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Label (e.g., Business, Owner - John Doe)"
            className="text-sm h-8 flex-1"
            data-testid="8821-label-input"
          />
          <input type="file" ref={unsignedFileRef} className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={handleUploadUnsigned} data-testid="8821-unsigned-file-input" />
          <Button size="sm" variant="outline" className="text-xs h-8 border-amber-400 text-amber-800 hover:bg-amber-100 whitespace-nowrap"
            onClick={() => unsignedFileRef.current?.click()} disabled={uploading} data-testid="8821-upload-unsigned-btn">
            {uploading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Upload className="h-3 w-3 mr-1" />}
            Upload 8821
          </Button>
        </div>
      )}

      {/* List of 8821 forms */}
      {form8821s.length > 0 ? (
        <div className="space-y-2 ml-6">
          {form8821s.map((form, i) => (
            <div key={form.form_id} className={`border rounded-lg p-3 ${form.status === 'signed' ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-amber-200'}`}
              data-testid={`8821-entry-${i}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${form.status === 'signed' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                  <span className="text-sm font-medium text-slate-800">{form.label}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${form.status === 'signed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {form.status === 'signed' ? 'Signed' : 'Awaiting Signature'}
                  </span>
                </div>
                {isUnderwriter && (
                  <button onClick={() => handleDelete(form.form_id)} disabled={deleting[form.form_id]}
                    className="text-slate-400 hover:text-red-500 transition-colors" data-testid={`8821-delete-${i}`}>
                    {deleting[form.form_id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  </button>
                )}
              </div>

              <div className="flex flex-col gap-1.5 mt-2 ml-4">
                {/* Unsigned file row */}
                {form.unsigned_file && (
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <FileText className="h-3 w-3 text-amber-500 flex-shrink-0" />
                    <span className="truncate flex-1">Unsigned: {form.unsigned_file.filename}</span>
                    <button onClick={() => handleDownload(form.unsigned_file.stored_filename, form.unsigned_file.filename)}
                      className="text-amber-600 hover:text-amber-800 flex-shrink-0" data-testid={`8821-download-unsigned-${i}`}>
                      <Download className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}

                {/* Signed file row */}
                {form.signed_file ? (
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <CheckCircle className="h-3 w-3 text-emerald-500 flex-shrink-0" />
                    <span className="truncate flex-1">Signed: {form.signed_file.filename}</span>
                    <button onClick={() => handleDownload(form.signed_file.stored_filename, form.signed_file.filename)}
                      className="text-emerald-600 hover:text-emerald-800 flex-shrink-0" data-testid={`8821-download-signed-${i}`}>
                      <Download className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <input type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      ref={el => signedFileRefs.current[form.form_id] = el}
                      onChange={(e) => handleUploadSigned(form.form_id, e)}
                      data-testid={`8821-signed-file-input-${i}`} />
                    <Button size="sm" variant="outline"
                      className="text-xs h-7 border-amber-400 text-amber-800 hover:bg-amber-100"
                      onClick={() => signedFileRefs.current[form.form_id]?.click()}
                      disabled={uploadingSigned[form.form_id]}
                      data-testid={`8821-upload-signed-${i}`}>
                      {uploadingSigned[form.form_id]
                        ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        : <Upload className="h-3 w-3 mr-1" />}
                      Upload Signed Copy
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-amber-600 italic ml-6">
          {isUnderwriter
            ? 'No 8821 forms uploaded yet. Use the form above to upload an unsigned 8821 for each entity or owner.'
            : 'No 8821 forms have been assigned to this application yet.'}
        </p>
      )}
    </div>
  );
};



/** Additional Documents upload section */
const AdditionalDocumentsSection = ({ applicationId, authToken, additionalDocuments = [], onUploadComplete, onRequestMove, requiredDocuments = [] }) => {
  const [docName, setDocName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState({});
  const fileInputRef = useRef(null);
  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

  // Detect when the user's typed label matches an existing required-doc slot
  // (case-insensitive). If they upload with this label the file will get
  // shoved into that required slot on the backend — this is the exact bug the
  // user has been battling. Warn them.
  const matchedRequiredSlot = (() => {
    const t = (docName || '').trim().toLowerCase();
    if (!t) return null;
    const hit = (requiredDocuments || []).find(d => (d.name || '').trim().toLowerCase() === t);
    return hit ? hit.name : null;
  })();

  const handleUploadClick = () => {
    if (!docName.trim()) {
      alert('Please enter a document description before uploading.');
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      alert('File exceeds the 25 MB limit');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(
        `${BACKEND_URL}/api/upload/${applicationId}?document_type=${encodeURIComponent(docName.trim())}`,
        formData, { headers }
      );
      setDocName('');
      if (onUploadComplete) onUploadComplete(docName.trim());
    } catch (err) {
      alert(err?.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteFile = async (name, storedFilename) => {
    const key = `${name}_${storedFilename}`;
    setDeleting(prev => ({ ...prev, [key]: true }));
    try {
      await axios.delete(
        `${BACKEND_URL}/api/applications/${applicationId}/documents/${encodeURIComponent(name)}/files/${encodeURIComponent(storedFilename)}`,
        { headers }
      );
      if (onUploadComplete) onUploadComplete(name);
    } catch (err) {
      alert('Failed to delete file');
    } finally {
      setDeleting(prev => ({ ...prev, [key]: false }));
    }
  };

  const getDownloadUrl = (storedFilename) => `/api/files/${applicationId}/${storedFilename}`;

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-5" data-testid="additional-documents-section">
      <div className="flex items-center gap-2 mb-3">
        <Plus className="h-4 w-4 text-slate-600" />
        <h4 className="text-sm font-bold text-slate-700">Additional Documents</h4>
      </div>
      <p className="text-xs text-slate-500 mb-3 ml-6">Upload any supporting documents not listed above. Max 25 MB per file.</p>

      {/* Upload form */}
      <div className="flex items-center gap-2 ml-6 mb-3">
        <Input
          value={docName}
          onChange={(e) => setDocName(e.target.value)}
          placeholder="Document description (e.g., Bank Reference Letter)"
          className={`text-sm h-8 flex-1 ${matchedRequiredSlot ? 'border-amber-400 focus-visible:ring-amber-400' : ''}`}
          data-testid="additional-doc-name-input"
        />
        <input type="file" ref={fileInputRef} className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
          onChange={handleFileSelected} data-testid="additional-doc-file-input" />
        <Button size="sm" variant="outline" className="text-xs h-8 border-slate-300 text-slate-700 hover:bg-slate-200 whitespace-nowrap"
          onClick={handleUploadClick} disabled={uploading} data-testid="additional-doc-upload-btn">
          {uploading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Upload className="h-3 w-3 mr-1" />}
          Upload
        </Button>
      </div>

      {matchedRequiredSlot && (
        <div className="ml-6 mb-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="additional-doc-name-collision-warning">
          <AlertCircle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold">Heads up:</span> &quot;{matchedRequiredSlot}&quot; is already a required-document slot above.
            If you upload with this label the file will land inside <span className="font-semibold">Documents Uploaded → {matchedRequiredSlot}</span>,
            not in Additional Documents. Rename it (e.g. add &quot;— Draft&quot; or the owner&apos;s name) to keep it separate.
          </div>
        </div>
      )}

      {/* List of uploaded additional documents */}
      {additionalDocuments.length > 0 && (
        <div className="space-y-2 ml-6">
          {additionalDocuments.map((adoc, i) => (
            <div key={adoc.name} className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`additional-doc-${i}`}>
              <div className="flex items-center gap-2 mb-1">
                <FileText className="h-3.5 w-3.5 text-slate-500 flex-shrink-0" />
                <span className="text-sm font-medium text-slate-700 flex-1">{adoc.name}</span>
                <span className="text-xs text-slate-400">{(adoc.files || []).length} file{(adoc.files || []).length !== 1 ? 's' : ''}</span>
              </div>
              {(adoc.files || []).map((f, fi) => (
                <div key={fi} className="flex items-center gap-2 text-xs text-slate-600 ml-5 py-1">
                  <FileText className="h-3 w-3 text-slate-400 flex-shrink-0" />
                  <span className="truncate flex-1">{f.filename}</span>
                  <a
                    href={getDownloadUrl(f.stored_filename)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 transition-colors flex-shrink-0"
                    data-testid={`additional-download-${i}-${fi}`}
                    title={`Download ${f.filename}`}
                  >
                    <Download className="h-3.5 w-3.5" />
                    Download
                  </a>
                  {onRequestMove && (
                    <button
                      onClick={() => onRequestMove(f, adoc.name)}
                      className="inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-400 hover:text-slate-800 hover:bg-slate-100 transition-colors flex-shrink-0"
                      data-testid={`additional-move-${i}-${fi}`}
                      title="Move to a different slot"
                      aria-label="Move file to a different slot"
                    >
                      <Move className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteFile(adoc.name, f.stored_filename)}
                    disabled={deleting[`${adoc.name}_${f.stored_filename}`]}
                    className="ml-1 inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0 disabled:opacity-50"
                    data-testid={`additional-delete-${i}-${fi}`}
                    title="Delete file"
                    aria-label="Delete file"
                  >
                    {deleting[`${adoc.name}_${f.stored_filename}`]
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Trash2 className="h-3.5 w-3.5" />}
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


/** Wraps the primary + 0..N additional Merchant Pre-MPAs.
 * Lets brokers/UWs add as many Pre-MPAs as needed (e.g. one per location or owner)
 * and download a combined PDF.
 */
const MerchantPreAppList = ({ doc, applicationId, authToken, primaryData, additionalEntries, onSave }) => {
  const [adding, setAdding] = useState(false);
  const [downloadingCombined, setDownloadingCombined] = useState(false);
  const totalCount = 1 + (additionalEntries?.length || 0);

  const handleAddAnother = async () => {
    setAdding(true);
    try {
      const headers = {};
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      await axios.post(
        `${BACKEND_URL}/api/applications/${applicationId}/additional-merchant-pre-apps`,
        { pre_app_data: {}, label: null },
        { headers }
      );
      if (onSave) onSave();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to add another Pre-MPA');
    } finally {
      setAdding(false);
    }
  };

  const handleDownloadCombined = async () => {
    setDownloadingCombined(true);
    try {
      const headers = {};
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      const resp = await axios.get(
        `${BACKEND_URL}/api/applications/${applicationId}/merchant-pre-apps/combined-pdf`,
        { headers, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `pre_mpa_combined_${applicationId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download combined PDF');
    } finally {
      setDownloadingCombined(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="mp-pre-app-list">
      <MerchantPreAppCard
        doc={doc}
        applicationId={applicationId}
        authToken={authToken}
        preAppData={primaryData || {}}
        onSave={onSave}
        slotMode="primary"
        title="Pre Merchant Processing Application"
      />
      {(additionalEntries || []).map((entry, idx) => (
        <MerchantPreAppCard
          key={entry.id || idx}
          doc={doc}
          applicationId={applicationId}
          authToken={authToken}
          preAppData={entry || {}}
          onSave={onSave}
          slotMode="additional"
          entryId={entry.id}
          entryLabel={entry.label}
          entryIndex={idx + 2}
          title={`Pre-MPA #${idx + 2}${entry.label ? ` — ${entry.label}` : (entry.legal_business_name ? ` — ${entry.legal_business_name}` : '')}`}
        />
      ))}
      <div className="flex items-center justify-between gap-2 pt-1">
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs border-cyan-400 text-cyan-700 hover:bg-cyan-50"
          onClick={handleAddAnother}
          disabled={adding}
          data-testid="mp-pre-app-add-another-btn"
        >
          {adding ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Plus className="h-3 w-3 mr-1" />}
          Add Another Pre-MPA
        </Button>
        {totalCount > 1 && (
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs border-slate-300 text-slate-700 hover:bg-slate-50"
            onClick={handleDownloadCombined}
            disabled={downloadingCombined}
            data-testid="mp-pre-app-combined-pdf-btn"
          >
            {downloadingCombined ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileDown className="h-3 w-3 mr-1" />}
            Download Combined PDF ({totalCount})
          </Button>
        )}
      </div>
    </div>
  );
};


/** Inline Merchant Pre-App viewer/editor card — merchant_processing only.
 * Supports both the primary Pre-MPA (slotMode='primary') and the additional
 * entries (slotMode='additional', entryId required).
 */
const MerchantPreAppCard = ({
  doc, applicationId, authToken, preAppData = {}, onSave,
  slotMode = 'primary', entryId = null, entryLabel = null, entryIndex = 1, title,
}) => {
  const [mode, setMode] = useState('view');
  const [data, setData] = useState(preAppData || {});
  const [labelDraft, setLabelDraft] = useState(entryLabel || '');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [generatingShare, setGeneratingShare] = useState(false);
  const [generatedShareLink, setGeneratedShareLink] = useState('');
  const isAdditional = slotMode === 'additional';

  useEffect(() => { setData(preAppData || {}); }, [preAppData]);
  useEffect(() => { setLabelDraft(entryLabel || ''); }, [entryLabel]);

  // Hide the auto-injected id/label from the form data (they're managed separately).
  const cleanData = (() => {
    const { id: _id, label: _l, ...rest } = data || {};
    return rest;
  })();

  const hasData = !!(cleanData && Object.keys(cleanData).some(k => {
    const v = cleanData[k];
    if (v === null || v === undefined || v === '') return false;
    if (typeof v === 'object') return Object.values(v).some(x => x !== null && x !== undefined && x !== '');
    return true;
  }));

  const set = (key, v) => setData(prev => ({ ...prev, [key]: v }));
  const setOwner = (n, key, v) => setData(prev => ({ ...prev, [`owner${n}`]: { ...(prev[`owner${n}`] || {}), [key]: v } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = {};
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      const { id: _id, label: _l, ...payload } = data || {};
      if (isAdditional) {
        await axios.put(
          `${BACKEND_URL}/api/applications/${applicationId}/additional-merchant-pre-apps/${entryId}`,
          { pre_app_data: payload, label: labelDraft || null },
          { headers }
        );
      } else {
        await axios.put(
          `${BACKEND_URL}/api/applications/${applicationId}/merchant-pre-app`,
          { pre_app_data: payload },
          { headers }
        );
      }
      setMode('view');
      if (onSave) onSave();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save pre-app');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isAdditional) return;
    if (!window.confirm(`Delete this Pre-MPA${entryLabel ? ` (${entryLabel})` : ''}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const headers = {};
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      await axios.delete(
        `${BACKEND_URL}/api/applications/${applicationId}/additional-merchant-pre-apps/${entryId}`,
        { headers }
      );
      if (onSave) onSave();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete Pre-MPA');
    } finally {
      setDeleting(false);
    }
  };

  const handleGenerateShareLink = async () => {
    setGeneratingShare(true);
    try {
      const headers = {};
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      const url = isAdditional
        ? `${BACKEND_URL}/api/applications/${applicationId}/additional-merchant-pre-apps/${entryId}/generate-link`
        : `${BACKEND_URL}/api/applications/${applicationId}/merchant-pre-app/generate-link`;
      const resp = await axios.post(url, {}, { headers });
      setGeneratedShareLink(`${window.location.origin}/merchant-pre-app/${resp.data.token}`);
    } catch (err) {
      alert('Failed to generate pre-app share link');
    } finally {
      setGeneratingShare(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const headers = {};
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      const url = isAdditional
        ? `${BACKEND_URL}/api/applications/${applicationId}/additional-merchant-pre-apps/${entryId}/pdf`
        : `${BACKEND_URL}/api/applications/mp-pre-app-pdf/${applicationId}`;
      const resp = await axios.get(url, { headers, responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      const fnameSlug = (entryLabel || cleanData.legal_business_name || (isAdditional ? `additional_${entryIndex}` : applicationId)).replace(/\s+/g, '_');
      a.download = `pre_mpa_${fnameSlug}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) { alert('Failed to download Pre-MPA PDF'); }
  };

  const o1 = cleanData.owner1 || {};
  const o2 = cleanData.owner2 || {};
  const headerTitle = title || 'Pre Merchant Processing Application';

  return (
    <div className={`bg-white border-2 ${isAdditional ? 'border-cyan-300' : 'border-cyan-200'} rounded-xl p-5`} data-testid={isAdditional ? `mp-pre-app-card-${entryIndex}` : 'mp-pre-app-card'}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <FileText className="h-4 w-4 text-cyan-700 shrink-0" />
          <h4 className="text-sm font-bold text-cyan-900 uppercase tracking-wide truncate">{headerTitle}</h4>
          {hasData && <span className="text-[10px] text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap">Saved</span>}
        </div>
        <div className="flex items-center gap-2">
          {hasData && (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleDownloadPdf} data-testid={isAdditional ? `mp-pre-app-download-pdf-btn-${entryIndex}` : 'mp-pre-app-download-pdf-btn'}>
              <FileDown className="h-3 w-3 mr-1" /> Download PDF
            </Button>
          )}
          <Button size="sm" variant="outline" className="h-7 text-xs border-teal-400 text-teal-700 hover:bg-teal-50" onClick={handleGenerateShareLink} disabled={generatingShare} data-testid={isAdditional ? `mp-pre-app-share-btn-${entryIndex}` : 'mp-pre-app-share-btn'}>
            {generatingShare ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Link2 className="h-3 w-3 mr-1" />} Share Input Form
          </Button>
          {mode === 'view' && (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setMode('edit')} data-testid={isAdditional ? `mp-pre-app-edit-btn-${entryIndex}` : 'mp-pre-app-edit-btn'}>
              <Pencil className="h-3 w-3 mr-1" /> {hasData ? 'Edit' : 'Add Data'}
            </Button>
          )}
          {isAdditional && (
            <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600 hover:bg-red-50" onClick={handleDelete} disabled={deleting} data-testid={`mp-pre-app-delete-btn-${entryIndex}`}>
              {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
            </Button>
          )}
        </div>
      </div>

      {generatedShareLink && (
        <div className="mb-3 p-2 bg-teal-50 border border-teal-200 rounded-lg flex items-center gap-2">
          <span className="text-[10px] text-teal-600 font-medium whitespace-nowrap">Pre-App Input Form:</span>
          <span className="text-xs text-teal-800 font-medium truncate flex-1" data-testid="mp-pre-app-share-link-text">{generatedShareLink}</span>
          <Button size="sm" variant="ghost" className="text-xs h-6 text-teal-700" onClick={() => { navigator.clipboard.writeText(generatedShareLink); alert('Link copied!'); }} data-testid="mp-pre-app-share-copy-btn">Copy</Button>
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-slate-400" onClick={() => setGeneratedShareLink('')}><X className="h-3 w-3" /></Button>
        </div>
      )}

      {mode === 'edit' ? (
        <div className="space-y-5" data-testid="mp-pre-app-form">
          {isAdditional && (
            <div>
              <Label className="text-xs font-bold text-slate-700 uppercase">Label / Nickname</Label>
              <Input
                value={labelDraft}
                onChange={e => setLabelDraft(e.target.value)}
                placeholder="e.g. Main St Cafe, Downtown Branch, Owner 2 - John Doe"
                className="mt-1"
                data-testid={`mp-pre-app-label-input-${entryIndex}`}
              />
              <p className="text-[10px] text-slate-500 mt-1 italic">Use this to distinguish multiple Pre-MPAs (e.g. by location or owner).</p>
            </div>
          )}
          <div>
            <h5 className="text-xs font-bold text-slate-700 uppercase mb-2 pb-1 border-b border-slate-200">Business Information</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label className="text-xs">Legal Business Name</Label><Input value={data.legal_business_name || ''} onChange={e => set('legal_business_name', e.target.value)} /></div>
              <div><Label className="text-xs">Federal Tax ID</Label><Input value={data.federal_tax_id || ''} onChange={e => set('federal_tax_id', e.target.value)} /></div>
              <div><Label className="text-xs">DBA Name</Label><Input value={data.dba_name || ''} onChange={e => set('dba_name', e.target.value)} /></div>
              <div><Label className="text-xs">Business Phone Number</Label><Input value={data.business_phone_number || ''} onChange={e => set('business_phone_number', e.target.value)} /></div>
              <div><Label className="text-xs">Physical Street Address</Label><Input value={data.physical_street_address || ''} onChange={e => set('physical_street_address', e.target.value)} /></div>
              <div><Label className="text-xs">Mobile Number</Label><Input value={data.mobile_number || ''} onChange={e => set('mobile_number', e.target.value)} /></div>
              <div><Label className="text-xs">City</Label><Input value={data.city || ''} onChange={e => set('city', e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">State</Label><Input value={data.state || ''} onChange={e => set('state', e.target.value)} /></div>
                <div><Label className="text-xs">Zip</Label><Input value={data.zip || ''} onChange={e => set('zip', e.target.value)} /></div>
              </div>
              <div className="md:col-span-2"><Label className="text-xs">E-Mail Address</Label><Input type="email" value={data.email_address || ''} onChange={e => set('email_address', e.target.value)} /></div>
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold text-slate-700 uppercase">Mail To</Label>
            <div className="flex flex-wrap gap-4 mt-2 mb-2">
              {['Business Address', 'Home Address', 'Other'].map(opt => (
                <label key={opt} className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input type="radio" name="mp-mail-to" checked={data.mail_to === opt} onChange={() => set('mail_to', opt)} />
                  {opt}
                </label>
              ))}
            </div>
            {data.mail_to === 'Other' && (
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-xs">City</Label><Input value={data.mail_to_other_city || ''} onChange={e => set('mail_to_other_city', e.target.value)} /></div>
                <div><Label className="text-xs">State</Label><Input value={data.mail_to_other_state || ''} onChange={e => set('mail_to_other_state', e.target.value)} /></div>
                <div><Label className="text-xs">Zip</Label><Input value={data.mail_to_other_zip || ''} onChange={e => set('mail_to_other_zip', e.target.value)} /></div>
              </div>
            )}
          </div>

          {[[1, o1], [2, o2]].map(([n, o]) => (
            <div key={n}>
              <h5 className="text-xs font-bold text-slate-700 uppercase mb-2 pb-1 border-b border-slate-200">Ownership Information — Owner {n}</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label className="text-xs">First Name</Label><Input value={o.first_name || ''} onChange={e => setOwner(n, 'first_name', e.target.value)} /></div>
                <div><Label className="text-xs">Last Name</Label><Input value={o.last_name || ''} onChange={e => setOwner(n, 'last_name', e.target.value)} /></div>
                <div><Label className="text-xs">Social Security Number</Label><Input value={o.social_security_number || ''} onChange={e => setOwner(n, 'social_security_number', e.target.value)} /></div>
                <div><Label className="text-xs">DOB</Label><Input value={o.dob || ''} onChange={e => setOwner(n, 'dob', e.target.value)} placeholder="MM/DD/YYYY" /></div>
                <div><Label className="text-xs">Home Address</Label><Input value={o.home_address || ''} onChange={e => setOwner(n, 'home_address', e.target.value)} /></div>
                <div><Label className="text-xs">City</Label><Input value={o.city || ''} onChange={e => setOwner(n, 'city', e.target.value)} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">State</Label><Input value={o.state || ''} onChange={e => setOwner(n, 'state', e.target.value)} /></div>
                  <div><Label className="text-xs">Zip</Label><Input value={o.zip || ''} onChange={e => setOwner(n, 'zip', e.target.value)} /></div>
                </div>
              </div>
            </div>
          ))}

          <div>
            <Label className="text-xs font-bold text-slate-700 uppercase">Additional Information / Comments</Label>
            <textarea rows={3} value={data.additional_comments || ''} onChange={e => set('additional_comments', e.target.value)} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400" />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => { setData(preAppData || {}); setMode('view'); }} disabled={saving}>Cancel</Button>
            <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white" onClick={handleSave} disabled={saving} data-testid="mp-pre-app-save-btn">
              {saving ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Saving...</> : 'Save'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="text-xs text-slate-600">
          {hasData ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
              {data.legal_business_name && <div><strong>Legal Business Name:</strong> {data.legal_business_name}</div>}
              {data.dba_name && <div><strong>DBA:</strong> {data.dba_name}</div>}
              {data.federal_tax_id && <div><strong>Federal Tax ID:</strong> {data.federal_tax_id}</div>}
              {data.business_phone_number && <div><strong>Business Phone:</strong> {data.business_phone_number}</div>}
              {data.email_address && <div><strong>Email:</strong> {data.email_address}</div>}
              {data.mail_to && <div><strong>Mail To:</strong> {data.mail_to}</div>}
              {(() => {
                const o1Name = `${o1.first_name || ''} ${o1.last_name || ''}`.trim() || o1.primary_owner_name;
                return o1Name ? <div><strong>Owner 1:</strong> {o1Name}</div> : null;
              })()}
              {(() => {
                const o2Name = `${o2.first_name || ''} ${o2.last_name || ''}`.trim() || o2.primary_owner_name;
                return o2Name ? <div><strong>Owner 2:</strong> {o2Name}</div> : null;
              })()}
            </div>
          ) : (
            <p className="text-slate-400 italic">No pre-app data entered yet. Click "Add Data" to fill it out, or "Share Input Form" to send to the merchant.</p>
          )}
        </div>
      )}
    </div>
  );
};


/** Inline debt schedule viewer/editor card */
const SHARE_DEBT_PRODUCTS = new Set([
  'sba_loan',
  'term_loan_3_5yr',
  'term_loan_10yr',
  'term_loc_hybrid',
  'jumbo_term_loan',
  'debt_consulting',
]);

const DebtScheduleCard = ({ doc, applicationId, authToken, debtSchedule, debtSchedulePdfUrl, onSave, productType }) => {
  const [mode, setMode] = useState('view'); // 'view' | 'edit'
  const [items, setItems] = useState(debtSchedule || []);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);
  const [generatingShare, setGeneratingShare] = useState(false);
  const [generatedShareLink, setGeneratedShareLink] = useState('');
  const uploadRef = React.useRef(null);

  useEffect(() => { setItems(debtSchedule || []); }, [debtSchedule]);

  const canShare = SHARE_DEBT_PRODUCTS.has(productType);

  // Remove a previously-uploaded debt schedule file. Backend guards deletion
  // (admin + senior UW always; broker owner only when app is incomplete).
  const handleDeleteFile = async (storedFilename, filename) => {
    if (!applicationId || !storedFilename) return;
    if (!window.confirm(`Delete "${filename || 'this file'}"? This cannot be undone.`)) return;
    setDeletingFile(storedFilename);
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      await axios.delete(
        `${BACKEND_URL}/api/applications/${applicationId}/documents/${encodeURIComponent(doc.name)}/files/${encodeURIComponent(storedFilename)}`,
        { headers },
      );
      if (onSave) onSave(items);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete file');
    } finally {
      setDeletingFile(null);
    }
  };

  const handleGenerateShareLink = async () => {
    setGeneratingShare(true);
    try {
      const headers = {};
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      const resp = await axios.post(`${BACKEND_URL}/api/applications/${applicationId}/debt-schedule/generate-link`, {}, { headers });
      const token = resp.data.token;
      setGeneratedShareLink(`${window.location.origin}/debt-schedule/${token}`);
    } catch (err) {
      alert('Failed to generate debt schedule share link');
    } finally {
      setGeneratingShare(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      await axios.put(`${BACKEND_URL}/api/applications/${applicationId}/debt-schedule`, { debt_schedule: items }, { headers });
      setMode('view');
      if (onSave) onSave(items);
    } catch (err) {
      console.error('Failed to save debt schedule:', err);
    } finally {
      setSaving(false);
    }
  };

  const [downloadingDebtPdf, setDownloadingDebtPdf] = useState(false);

  const handleDownloadPdf = async () => {
    if (downloadingDebtPdf) return;
    setDownloadingDebtPdf(true);
    try {
      const headers = {};
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      const res = await axios.get(
        `${BACKEND_URL}/api/applications/debt-schedule-pdf/${applicationId}`,
        { headers, responseType: 'blob', timeout: 60000 }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `debt_schedule_${applicationId}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);  // attach so Safari/iPad fires the click reliably
      link.click();
      // Delay cleanup so the browser has time to start the download
      setTimeout(() => {
        try { document.body.removeChild(link); } catch (_e) { /* already removed */ }
        window.URL.revokeObjectURL(blobUrl);
      }, 250);
    } catch (err) {
      console.error('Failed to download debt schedule PDF:', err);
      alert('Failed to download Debt Schedule PDF. Please try again.');
    } finally {
      setDownloadingDebtPdf(false);
    }
  };

  const handleUploadDebtSchedule = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const headers = {};
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      await axios.post(`${BACKEND_URL}/api/upload/${applicationId}?document_type=${encodeURIComponent('Business Debt Schedule')}`, formData, { headers });
      if (onSave) onSave(items);
    } catch (err) {
      console.error('Failed to upload debt schedule:', err);
    } finally {
      setUploading(false);
      if (uploadRef.current) uploadRef.current.value = '';
    }
  };

  const addItem = () => {
    setItems(prev => [...prev, {
      lender_name: '', type_of_debt: '', original_amount: null, current_balance: null,
      interest_rate: null, monthly_payment: null, open_date: '', maturity_date: '',
      secured_or_unsecured: '', current_or_delinquent: '', type_of_collateral: ''
    }]);
  };

  const updateItem = (index, field, value) => {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const removeItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const fmtCurrency = (v) => v != null ? `$${Number(v).toLocaleString()}` : '—';

  const hasData = items.length > 0;
  const pdfUrl = debtSchedulePdfUrl
    ? debtSchedulePdfUrl
    : `/api/applications/debt-schedule-pdf/${applicationId}`;

  return (
    <div className={`border rounded-xl p-5 ${hasData ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'}`} data-testid="debt-schedule-card">
      <div className="flex items-center gap-2 mb-3">
        <DollarSign className={`h-4 w-4 ${hasData ? 'text-blue-600' : 'text-orange-600'}`} />
        <h4 className={`text-sm font-bold ${hasData ? 'text-blue-800' : 'text-orange-800'}`}>{doc.name}</h4>
        {hasData && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">{items.length} {items.length === 1 ? 'debt' : 'debts'}</span>}
        <div className="ml-auto flex gap-1.5">
          {hasData && (
            <Button size="sm" variant="outline" className="h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-100" onClick={handleDownloadPdf} disabled={downloadingDebtPdf} data-testid="debt-schedule-download-pdf">
              {downloadingDebtPdf ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileDown className="h-3 w-3 mr-1" />} Download PDF
            </Button>
          )}
          <Button size="sm" variant="outline" className="h-7 text-xs border-slate-300 text-slate-600 hover:bg-slate-100" onClick={() => uploadRef.current?.click()} disabled={uploading} data-testid="debt-schedule-upload-btn">
            {uploading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />} Upload
          </Button>
          <input ref={uploadRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" onChange={handleUploadDebtSchedule} />
          {canShare && (
            <Button size="sm" variant="outline" className="h-7 text-xs border-teal-400 text-teal-700 hover:bg-teal-50" onClick={handleGenerateShareLink} disabled={generatingShare} data-testid="debt-schedule-share-btn">
              {generatingShare ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Link2 className="h-3 w-3 mr-1" />}
              Share Input Form
            </Button>
          )}
          {mode === 'view' && (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setMode('edit')} data-testid="debt-schedule-edit-btn">
              <Pencil className="h-3 w-3 mr-1" /> {hasData ? 'Edit' : 'Add Data'}
            </Button>
          )}
        </div>
      </div>

      {generatedShareLink && (
        <div className="mb-3 p-2 bg-teal-50 border border-teal-200 rounded-lg flex items-center gap-2">
          <span className="text-[10px] text-teal-600 font-medium whitespace-nowrap">Debt Schedule Input Form:</span>
          <span className="text-xs text-teal-800 font-medium truncate flex-1" data-testid="debt-share-link-text">{generatedShareLink}</span>
          <Button size="sm" variant="ghost" className="text-xs h-6 text-teal-700" onClick={() => { navigator.clipboard.writeText(generatedShareLink); alert('Link copied!'); }} data-testid="debt-share-copy-btn">
            Copy
          </Button>
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-slate-400" onClick={() => setGeneratedShareLink('')}>
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      {/* View mode */}
      {mode === 'view' && hasData && (
        <div className="overflow-x-auto ml-1">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200">
                <th className="pb-1.5 pr-3 font-medium">Lender</th>
                <th className="pb-1.5 pr-3 font-medium">Type</th>
                <th className="pb-1.5 pr-3 font-medium text-right">Balance</th>
                <th className="pb-1.5 pr-3 font-medium text-right">Rate</th>
                <th className="pb-1.5 pr-3 font-medium text-right">Monthly Pmt</th>
                <th className="pb-1.5 pr-3 font-medium">Origin</th>
                <th className="pb-1.5 pr-3 font-medium">Maturity</th>
                <th className="pb-1.5 pr-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={i} className="border-b border-slate-100 last:border-0">
                  <td className="py-1.5 pr-3 text-slate-700">{item.lender_name || '—'}</td>
                  <td className="py-1.5 pr-3 text-slate-600">{item.type_of_debt || '—'}</td>
                  <td className="py-1.5 pr-3 text-right text-slate-700">{fmtCurrency(item.current_balance)}</td>
                  <td className="py-1.5 pr-3 text-right text-slate-600">{item.interest_rate != null ? `${item.interest_rate}%` : '—'}</td>
                  <td className="py-1.5 pr-3 text-right text-slate-700">{fmtCurrency(item.monthly_payment)}</td>
                  <td className="py-1.5 pr-3 text-slate-600">{item.open_date || '—'}</td>
                  <td className="py-1.5 pr-3 text-slate-600">{item.maturity_date || '—'}</td>
                  <td className="py-1.5 pr-3 text-slate-600">{item.current_or_delinquent || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {mode === 'view' && !hasData && (
        <p className="text-xs text-orange-700 ml-6">No debt schedule data entered yet. Click "Add Data" to get started, or upload a file.</p>
      )}

      {/* Uploaded debt schedule files */}
      {(doc.files || []).length > 0 && mode === 'view' && (
        <div className="mt-3 ml-1 space-y-1">
          <p className="text-xs font-medium text-slate-500 mb-1">Uploaded Files:</p>
          {doc.files.map((f, fi) => (
            <div key={f.stored_filename || fi} className="flex items-center gap-2 text-xs text-slate-600 bg-white border border-blue-100 rounded-lg px-3 py-1.5">
              <FileText className="h-3 w-3 text-blue-500 flex-shrink-0" />
              <span className="truncate flex-1">{f.filename}</span>
              {applicationId && f.stored_filename && (
                <a href={`/api/files/${applicationId}/${f.stored_filename}`} target="_blank" rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 flex-shrink-0" title="Download file" data-testid={`debt-file-download-${fi}`}>
                  <Download className="h-3 w-3" />
                </a>
              )}
              {applicationId && f.stored_filename && (
                <button
                  type="button"
                  onClick={() => handleDeleteFile(f.stored_filename, f.filename)}
                  disabled={deletingFile === f.stored_filename}
                  className="text-rose-500 hover:text-rose-700 disabled:opacity-50 flex-shrink-0"
                  title="Delete file"
                  data-testid={`debt-file-delete-${fi}`}
                >
                  {deletingFile === f.stored_filename
                    ? <Loader2 className="h-3 w-3 animate-spin" />
                    : <Trash2 className="h-3 w-3" />}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit mode */}
      {mode === 'edit' && (
        <div className="space-y-3">
          {items.map((item, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-lg p-3 relative" data-testid={`debt-item-${i}`}>
              <button onClick={() => removeItem(i)} className="absolute top-2 right-2 text-slate-400 hover:text-red-500" data-testid={`remove-debt-${i}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                <div>
                  <label className="text-[10px] text-slate-500 block mb-0.5">Lender Name</label>
                  <Input className="h-7 text-xs" value={item.lender_name || ''} onChange={e => updateItem(i, 'lender_name', e.target.value)} />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-0.5">Type of Debt</label>
                  <Input className="h-7 text-xs" value={item.type_of_debt || ''} onChange={e => updateItem(i, 'type_of_debt', e.target.value)} />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-0.5">Original Amount</label>
                  <Input className="h-7 text-xs" type="number" value={item.original_amount ?? ''} onChange={e => updateItem(i, 'original_amount', e.target.value ? parseFloat(e.target.value) : null)} />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-0.5">Current Balance</label>
                  <Input className="h-7 text-xs" type="number" value={item.current_balance ?? ''} onChange={e => updateItem(i, 'current_balance', e.target.value ? parseFloat(e.target.value) : null)} />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-0.5">Interest Rate (%)</label>
                  <Input className="h-7 text-xs" type="number" step="0.01" value={item.interest_rate ?? ''} onChange={e => updateItem(i, 'interest_rate', e.target.value ? parseFloat(e.target.value) : null)} />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-0.5">Monthly Payment</label>
                  <Input className="h-7 text-xs" type="number" value={item.monthly_payment ?? ''} onChange={e => updateItem(i, 'monthly_payment', e.target.value ? parseFloat(e.target.value) : null)} />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-0.5">Secured/Unsecured</label>
                  <Input className="h-7 text-xs" value={item.secured_or_unsecured || ''} onChange={e => updateItem(i, 'secured_or_unsecured', e.target.value)} />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-0.5">Current/Delinquent</label>
                  <Input className="h-7 text-xs" value={item.current_or_delinquent || ''} onChange={e => updateItem(i, 'current_or_delinquent', e.target.value)} />
                </div>
                <div className="min-w-0 overflow-hidden">
                  <label className="text-[10px] text-slate-500 block mb-0.5">Origin Date</label>
                  <Input className="h-7 text-xs w-full min-w-0" type="date" value={item.open_date || ''} onChange={e => updateItem(i, 'open_date', e.target.value)} />
                </div>
                <div className="min-w-0 overflow-hidden">
                  <label className="text-[10px] text-slate-500 block mb-0.5">Maturity Date</label>
                  <Input className="h-7 text-xs w-full min-w-0" type="date" value={item.maturity_date || ''} onChange={e => updateItem(i, 'maturity_date', e.target.value)} />
                </div>
              </div>
            </div>
          ))}
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="text-xs" onClick={addItem} data-testid="add-debt-item-btn">
              <Plus className="h-3 w-3 mr-1" /> Add Debt
            </Button>
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="ghost" className="text-xs" onClick={() => { setItems(debtSchedule || []); setMode('view'); }}>Cancel</Button>
              <Button size="sm" className="text-xs bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSave} disabled={saving} data-testid="save-debt-schedule-btn">
                {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null} Save & Generate PDF
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Personal Financial Statement Section (Multi-Owner) ────────────────

const PFS_EMPTY = {
  name: '',
  cash_in_bank_accounts: '',
  marketable_securities: '',
  cash_surrender_value_life_insurance: '',
  retirement_accounts: '',
  real_estate_owned: '',
  other_assets: '',
  credit_cards: '',
  installment_loans: '',
  real_estate_loans: '',
  taxes_owed: '',
  other_liabilities: '',
};

const ASSET_FIELDS = [
  { key: 'cash_in_bank_accounts', label: 'Cash in Bank Accounts' },
  { key: 'marketable_securities', label: 'Marketable Securities' },
  { key: 'cash_surrender_value_life_insurance', label: 'Cash Surrender Value of Life Insurance' },
  { key: 'retirement_accounts', label: 'Retirement Accounts' },
  { key: 'real_estate_owned', label: 'Real Estate Owned' },
  { key: 'other_assets', label: 'Other Assets' },
];

const LIABILITY_FIELDS = [
  { key: 'credit_cards', label: 'Credit Cards' },
  { key: 'installment_loans', label: 'Installment Loans' },
  { key: 'real_estate_loans', label: 'Real Estate Loans' },
  { key: 'taxes_owed', label: 'Taxes Owed' },
  { key: 'other_liabilities', label: 'Other Liabilities' },
];

const numVal = (v) => parseFloat(v) || 0;
const fmtMoney = (v) => `$${numVal(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PFSEntryView = ({ entry, onEdit, onDelete, onDownloadPdf, applicationId, authToken }) => {
  const totalAssets = ASSET_FIELDS.reduce((s, f) => s + numVal(entry[f.key]), 0);
  const totalLiabilities = LIABILITY_FIELDS.reduce((s, f) => s + numVal(entry[f.key]), 0);
  const netWorth = totalAssets - totalLiabilities;
  const hasData = totalAssets > 0 || totalLiabilities > 0;
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const resp = await axios.get(`${BACKEND_URL}/api/applications/pfs-pdf/${applicationId}/${entry.pfs_id}`, {
        headers, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `PFS_${(entry.name || 'owner').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download PDF. Try saving the entry first.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="border border-indigo-200 rounded-lg p-3 bg-white" data-testid={`pfs-entry-${entry.pfs_id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {hasData ? <CheckCircle className="h-3.5 w-3.5 text-green-600" /> : <FileText className="h-3.5 w-3.5 text-indigo-500" />}
          <span className="text-sm font-semibold text-slate-800">{entry.name || 'Unnamed Owner'}</span>
          {entry.submitted_via_link && <span className="text-[10px] bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded">Via Link</span>}
        </div>
        <div className="flex items-center gap-1.5">
          {hasData && (
            <button onClick={handleDownloadPdf} disabled={downloading}
              className="flex items-center gap-1 text-[11px] text-indigo-600 hover:text-indigo-800 border border-indigo-200 rounded px-1.5 py-0.5 hover:bg-indigo-50 disabled:opacity-50"
              data-testid={`pfs-pdf-${entry.pfs_id}`}>
              {downloading ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileDown className="h-3 w-3" />} PDF
            </button>
          )}
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-indigo-600" onClick={() => onEdit(entry)} data-testid={`pfs-edit-${entry.pfs_id}`}>
            <Pencil className="h-3 w-3" />
          </Button>
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={() => onDelete(entry.pfs_id)} data-testid={`pfs-delete-${entry.pfs_id}`}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
      {hasData && (
        <div className="grid grid-cols-3 gap-3 text-xs">
          <div className="bg-green-50 rounded p-2">
            <div className="text-[10px] text-green-600 font-semibold uppercase">Total Assets</div>
            <div className="text-sm font-bold text-green-800">{fmtMoney(totalAssets)}</div>
          </div>
          <div className="bg-red-50 rounded p-2">
            <div className="text-[10px] text-red-600 font-semibold uppercase">Total Liabilities</div>
            <div className="text-sm font-bold text-red-800">{fmtMoney(totalLiabilities)}</div>
          </div>
          <div className="bg-slate-800 rounded p-2">
            <div className="text-[10px] text-slate-300 font-semibold uppercase">Net Worth</div>
            <div className={`text-sm font-bold ${netWorth >= 0 ? 'text-green-400' : 'text-red-400'}`}>{fmtMoney(netWorth)}</div>
          </div>
        </div>
      )}
    </div>
  );
};

const PFSEditForm = ({ initialData, onSave, onCancel, saving }) => {
  const [data, setData] = useState({ ...PFS_EMPTY, ...initialData });
  const totalAssets = ASSET_FIELDS.reduce((s, f) => s + numVal(data[f.key]), 0);
  const totalLiabilities = LIABILITY_FIELDS.reduce((s, f) => s + numVal(data[f.key]), 0);
  const netWorth = totalAssets - totalLiabilities;

  return (
    <div className="border border-indigo-300 rounded-lg p-4 bg-indigo-50" data-testid="pfs-edit-form">
      <div className="mb-3">
        <label className="text-xs font-semibold text-slate-600 block mb-1">Owner / Guarantor Name *</label>
        <Input className="h-8 text-sm" value={data.name || ''} onChange={e => setData(d => ({ ...d, name: e.target.value }))} placeholder="Full legal name" data-testid="pfs-name-input" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <h5 className="text-xs font-bold text-green-700 uppercase tracking-wide mb-2">Assets</h5>
          {ASSET_FIELDS.map(f => (
            <div key={f.key} className="mb-2">
              <label className="text-[10px] text-slate-500 block mb-0.5">{f.label}</label>
              <Input className="h-7 text-xs" type="number" value={data[f.key] ?? ''} placeholder="0.00"
                onChange={e => setData(d => ({ ...d, [f.key]: e.target.value }))} data-testid={`pfs-${f.key}-input`} />
            </div>
          ))}
          <div className="flex justify-between py-1.5 bg-green-100 rounded px-2">
            <span className="text-xs font-bold text-green-800">Total Assets</span>
            <span className="text-xs font-bold text-green-800">{fmtMoney(totalAssets)}</span>
          </div>
        </div>
        <div>
          <h5 className="text-xs font-bold text-red-700 uppercase tracking-wide mb-2">Liabilities</h5>
          {LIABILITY_FIELDS.map(f => (
            <div key={f.key} className="mb-2">
              <label className="text-[10px] text-slate-500 block mb-0.5">{f.label}</label>
              <Input className="h-7 text-xs" type="number" value={data[f.key] ?? ''} placeholder="0.00"
                onChange={e => setData(d => ({ ...d, [f.key]: e.target.value }))} data-testid={`pfs-${f.key}-input`} />
            </div>
          ))}
          <div className="flex justify-between py-1.5 bg-red-100 rounded px-2">
            <span className="text-xs font-bold text-red-800">Total Liabilities</span>
            <span className="text-xs font-bold text-red-800">{fmtMoney(totalLiabilities)}</span>
          </div>
        </div>
      </div>
      <div className="mt-3 mb-3">
        <div className="flex justify-between py-2 px-3 bg-[#081120] rounded-lg">
          <span className="text-sm font-bold text-white">Net Worth</span>
          <span className={`text-sm font-bold ${netWorth >= 0 ? 'text-green-400' : 'text-red-400'}`}>{fmtMoney(netWorth)}</span>
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <Button size="sm" variant="ghost" className="text-xs" onClick={onCancel}>Cancel</Button>
        <Button size="sm" className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => onSave(data)} disabled={saving || !data.name} data-testid="save-pfs-btn">
          {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null} Save
        </Button>
      </div>
    </div>
  );
};

const PFSCard = ({ doc, applicationId, authToken, pfsData, pfsPdfUrl, onSave, canPush, onUpload }) => {
  // pfsData is now the full pfs_list array (or legacy single object)
  const [pfsList, setPfsList] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null); // null=none, 'new'=adding, or entry object
  const [saving, setSaving] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [uploadingPfs, setUploadingPfs] = useState(false);
  const pfsFileInputRef = useRef(null);

  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

  useEffect(() => {
    // pfsData can be an array (new) or object (legacy)
    if (Array.isArray(pfsData)) {
      setPfsList(pfsData);
    } else if (pfsData && typeof pfsData === 'object' && Object.keys(pfsData).length > 0) {
      // Legacy single PFS
      setPfsList([{ ...pfsData, pfs_id: pfsData.pfs_id || 'legacy' }]);
    } else {
      setPfsList([]);
    }
  }, [pfsData]);

  const handleSave = async (data) => {
    setSaving(true);
    try {
      const isEditing = editingEntry && editingEntry !== 'new' && editingEntry.pfs_id;
      const payload = { pfs_data: data, pfs_id: isEditing ? editingEntry.pfs_id : null };
      const resp = await axios.put(`${BACKEND_URL}/api/applications/${applicationId}/pfs`, payload, { headers });
      setPfsList(resp.data.pfs_list || []);
      setEditingEntry(null);
      if (onSave) onSave();
    } catch (err) {
      console.error('PFS save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pfsId) => {
    if (!window.confirm('Remove this personal financial statement?')) return;
    try {
      const resp = await axios.delete(`${BACKEND_URL}/api/applications/${applicationId}/pfs/${pfsId}`, { headers });
      setPfsList(resp.data.pfs_list || []);
      if (onSave) onSave();
    } catch (err) {
      console.error('PFS delete error:', err);
    }
  };

  const handleGenerateLink = async () => {
    setGeneratingLink(true);
    try {
      const resp = await axios.post(`${BACKEND_URL}/api/applications/${applicationId}/pfs/generate-link`, {}, { headers });
      const token = resp.data.token;
      setGeneratedLink(`${window.location.origin}/pfs/${token}`);
    } catch (err) {
      alert('Failed to generate PFS link');
    } finally {
      setGeneratingLink(false);
    }
  };

  const handlePfsFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      alert('File exceeds the 25 MB limit');
      return;
    }
    setUploadingPfs(true);
    try {
      const docName = doc.name || 'Personal Financial Statement';
      const formData = new FormData();
      formData.append('file', file);
      const url = `${BACKEND_URL}/api/upload/${applicationId}?document_type=${encodeURIComponent(docName)}`;
      await axios.post(url, formData, { headers });
      if (onSave) onSave();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploadingPfs(false);
      if (pfsFileInputRef.current) pfsFileInputRef.current.value = '';
    }
  };

  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5" data-testid="pfs-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-indigo-600" />
          <h4 className="text-sm font-bold text-indigo-900">{doc.name || 'Personal Financial Statements'}</h4>
          <span className="text-[10px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full font-medium">{pfsList.length} owner{pfsList.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="file" ref={pfsFileInputRef} className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={handlePfsFileUpload} data-testid="pfs-file-input" />
          <Button size="sm" variant="outline" className="text-xs h-7 border-slate-300 text-slate-700 hover:bg-slate-100"
            onClick={() => pfsFileInputRef.current?.click()} disabled={uploadingPfs} data-testid="upload-pfs-doc">
            {uploadingPfs ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Upload className="h-3 w-3 mr-1" />}
            Upload PFS
          </Button>
          <Button size="sm" variant="outline" className="text-xs h-7 border-teal-400 text-teal-700 hover:bg-teal-50"
            onClick={handleGenerateLink} disabled={generatingLink} data-testid="generate-pfs-link">
            {generatingLink ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Link2 className="h-3 w-3 mr-1" />}
            Share Input Form
          </Button>
          {editingEntry === null && (
            <Button size="sm" className="text-xs h-7 bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={() => setEditingEntry('new')} data-testid="add-pfs-owner">
              <Plus className="h-3 w-3 mr-1" /> Add Owner
            </Button>
          )}
        </div>
      </div>

      {generatedLink && (
        <div className="mb-3 p-2 bg-teal-50 border border-teal-200 rounded-lg flex items-center gap-2">
          <span className="text-[10px] text-teal-600 font-medium whitespace-nowrap">PFS Input Form:</span>
          <span className="text-xs text-teal-800 font-medium truncate flex-1">{generatedLink}</span>
          <Button size="sm" variant="ghost" className="text-xs h-6 text-teal-700" onClick={() => { navigator.clipboard.writeText(generatedLink); alert('Link copied!'); }}>
            Copy
          </Button>
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-slate-400" onClick={() => setGeneratedLink('')}>
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      <div className="space-y-2">
        {/* Uploaded PFS file attachments */}
        {(doc.files || []).length > 0 && (
          <div className="bg-white border border-indigo-200 rounded-lg p-3" data-testid="pfs-uploaded-files">
            <div className="flex items-center gap-2 mb-1.5">
              <CheckCircle className="h-3.5 w-3.5 text-green-600" />
              <span className="text-sm font-semibold text-slate-700">Uploaded PFS Documents</span>
              <span className="text-xs text-slate-400">{doc.files.length} file{doc.files.length !== 1 ? 's' : ''}</span>
            </div>
            {doc.files.map((f, fi) => (
              <div key={fi} className="flex items-center gap-2 text-xs text-slate-600 ml-5 py-0.5">
                <FileText className="h-3 w-3 text-indigo-400 flex-shrink-0" />
                <span className="truncate flex-1">{f.filename}</span>
                <button onClick={async () => {
                  try {
                    const resp = await fetch(`${BACKEND_URL}/api/files/${applicationId}/${f.stored_filename}`, {
                      headers: { Authorization: `Bearer ${authToken}` }
                    });
                    if (!resp.ok) throw new Error();
                    const blob = await resp.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url; a.download = f.filename;
                    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                  } catch { alert('Download failed'); }
                }} className="text-indigo-500 hover:text-indigo-800 flex-shrink-0" data-testid={`pfs-file-download-${fi}`}>
                  <Download className="h-3.5 w-3.5" />
                </button>
                <button onClick={async () => {
                  try {
                    const docName = doc.name || 'Personal Financial Statement';
                    await axios.delete(`${BACKEND_URL}/api/applications/${applicationId}/documents/${encodeURIComponent(docName)}/files/${encodeURIComponent(f.stored_filename)}`, { headers });
                    if (onSave) onSave();
                  } catch { alert('Delete failed'); }
                }} className="text-slate-400 hover:text-red-500 flex-shrink-0 transition-colors" data-testid={`pfs-file-delete-${fi}`}>
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {pfsList.map(entry => (
          editingEntry && editingEntry.pfs_id === entry.pfs_id ? (
            <PFSEditForm key={entry.pfs_id} initialData={entry} onSave={handleSave} onCancel={() => setEditingEntry(null)} saving={saving} />
          ) : (
            <PFSEntryView key={entry.pfs_id} entry={entry} applicationId={applicationId} authToken={authToken}
              onEdit={(e) => setEditingEntry(e)} onDelete={handleDelete} />
          )
        ))}

        {editingEntry === 'new' && (
          <PFSEditForm initialData={{}} onSave={handleSave} onCancel={() => setEditingEntry(null)} saving={saving} />
        )}

        {pfsList.length === 0 && editingEntry === null && (
          <p className="text-xs text-indigo-600 italic py-2">No personal financial statements entered yet. Click "Add Owner" to begin, "Upload PFS" to attach an existing document, or "Share Input Form" to send a form to an owner.</p>
        )}
      </div>
    </div>
  );
};


export default DocumentUploader;
